import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Music as MusicIcon, Play, Apple, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { useConfig } from '../config/useConfig';

const Music = () => {
  const { language } = useLanguage();
  const t = translations[language].music;
  const config = useConfig();

  return (
    <section id="music" className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-secondary text-sm font-semibold tracking-widest uppercase">{t.subtitle}</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 mb-6">
            {t.title.split('—')[0]}— <span className="text-glow-fire" style={{ color: 'hsl(6 55% 45%)' }}>{t.title.split('—')[1]}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Album Cover & Streaming Links */}
          <div className="space-y-8">
            {/* Album Cover */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-br from-secondary via-primary to-secondary rounded-2xl blur-xl opacity-40 group-hover:opacity-60 transition-all fire-glow" />
              <div className="relative aspect-square rounded-2xl overflow-hidden border border-border/50">
                <img
                  src={config.images.albumCover}
                  alt="Divine Fire album cover: burning eclipse and gold cathedral"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-bold text-glow-fire" style={{ color: 'hsl(6 55% 45%)' }}>{t.subtitle}</h3>
                  <p className="text-foreground/80 mt-1">Eclipse Heaven • 2025</p>
                </div>
              </div>
            </div>

            {/* Streaming Links */}
            <Card className="p-6 bg-card/50 backdrop-blur border-border/50">
              <h3 className="text-xl font-semibold mb-4 text-primary">{t.listenNow}</h3>
              <div className="space-y-3">
                <Button
                  className="w-full justify-start divine-glow bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => window.open(config.links.spotifyAlbum, '_blank')}
                >
                  <MusicIcon className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">Spotify</span>
                  <ExternalLink className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start border-secondary/50 text-secondary hover:bg-secondary/10 fire-glow"
                  onClick={() => window.open(config.links.youtube, '_blank')}
                >
                  <Play className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">YouTube Music</span>
                  <ExternalLink className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start border-accent/50 text-accent hover:bg-accent/10 soul-glow"
                  onClick={() => window.open(config.links.appleMusic, '_blank')}
                >
                  <Apple className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">Apple Music</span>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          </div>

          {/* Tracklist */}
          <div>
            <Card className="p-6 sm:p-8 bg-card/30 backdrop-blur border-border/50">
              <h3 className="text-2xl font-semibold mb-6 text-primary">{t.tracklist}</h3>
              <div className="space-y-3">
                {t.tracks.map((track, index) => (
                  <div
                    key={index}
                    className="group flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-all cursor-pointer"
                    style={{
                      animationDelay: `${index * 50}ms`,
                    }}
                  >
                    <span className="text-primary/60 font-mono text-sm font-semibold min-w-[2rem]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {track.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">{track.desc}</p>
                    </div>
                    <Play className="h-4 w-4 text-primary/50 group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100" />
                  </div>
                ))}
              </div>
            </Card>

            {/* Embedded Player Placeholder */}
            <Card className="mt-6 p-6 bg-card/30 backdrop-blur border-border/50">
              <div className="aspect-video bg-muted/20 rounded-lg flex items-center justify-center border border-border/30">
                <div className="text-center">
                  <Play className="h-12 w-12 text-primary/50 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Spotify Player Embed</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Replace with actual embed URL</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Music;